import type { SxProps, Theme } from "@mui/material";
import { Box } from "@mui/material";
import { getCurrencySymbol } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { CSSProperties } from "react";

interface CurrencyIconProps {
    /**
     * MUI sx prop for styling
     */
    sx?: SxProps<Theme>;
    /**
     * Standard CSS style prop
     */
    style?: CSSProperties;

    /**
     * Optional currency code to override company currency
     */
    currency?: string;
}

/**
 * Dynamic currency icon component that displays the company's currency symbol
 * instead of a hardcoded euro symbol. Can be used as a drop-in replacement
 * for EuroIcon from Material-UI.
 *
 * @example
 * ```tsx
 * <CurrencyIcon sx={{ fontSize: 16 }} />
 * <CurrencyIcon style={{ width: "0.87rem" }} />
 * ```
 */
const CurrencyIcon = ({ sx, style, currency }: CurrencyIconProps) => {
    const { companyInfos } = useCompanySettings();
    const currencySymbol = getCurrencySymbol(currency ?? companyInfos?.currency ?? "EUR");

    return (
        <Box
            component="span"
            sx={{
                fontWeight: 400,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "16px",
                ...sx,
            }}
            style={style}
        >
            {currencySymbol}
        </Box>
    );
};

export default CurrencyIcon;
